import { useEffect, useMemo, useState } from "react";
import { useModuleVisibility } from "../hooks/useModuleVisibility";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash, PencilSimple, Buildings, DownloadSimple, UploadSimple, Sparkle, FileXls, FilePdf, ClockCounterClockwise, Warning, BatteryCharging } from "@phosphor-icons/react";

const STATUS_META = {
  ok: { cls: "bg-green-50 text-green-700 border-green-300", label: "On schedule" },
  due_soon: { cls: "bg-amber-50 text-amber-700 border-amber-300", label: "Due soon" },
  overdue: { cls: "bg-red-50 text-red-700 border-red-300", label: "Overdue" },
  no_date: { cls: "bg-secondary text-gray-600 border-gray-300", label: "No date" },
  no_maintenance: { cls: "bg-blue-50 text-blue-700 border-blue-300", label: "No periodic maint." },
};
function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.no_date;
  return <span data-testid={`device-status-${status}`} className={`inline-block border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${m.cls}`}>{m.label}</span>;
}

const emptySub = {
  name: "", location: "", notes: "", has_protective_relay: true, has_communications: false,
  has_vt_ct: false, has_station_dc: false, has_control_circuitry: false,
  has_auto_reclosing: false, has_sudden_pressure: false,
};
const SUB_QUALIFIERS = [
  ["has_protective_relay", "Protective Relays"], ["has_communications", "Communications Systems"],
  ["has_vt_ct", "Voltage & Current Sensing"], ["has_station_dc", "Station DC Supply / Batteries"],
  ["has_control_circuitry", "Control Circuitry"], ["has_auto_reclosing", "Automatic Reclosing"],
  ["has_sudden_pressure", "Sudden Pressure Relaying"],
];
const emptyDev = {
  substation_id: "", component_type: "Protective Relay", activity_code: "", ras: false, ufls: false, uvls: false,
  unique_identifier: "", manufacturer_model: "", monitored_status: "Unmonitored", transitioned_date: "",
  legacy_interval_years: "", comments: "", most_recent_date: "", most_recent_evidence: "",
  previous_date: "", previous_evidence: "", target_interval_months: "",
};

export default function Prc005() {
  const { canEdit } = useAuth();
  const { isSubHidden } = useModuleVisibility();
  const [subs, setSubs] = useState([]);
  const [devices, setDevices] = useState([]);
  const [ref, setRef] = useState({ activity_codes: [], component_types: [], monitored_status: [] });
  const [summary, setSummary] = useState({ substations: 0, devices: 0, status: {} });
  const [filterSub, setFilterSub] = useState("all");

  const [subOpen, setSubOpen] = useState(false);
  const [subForm, setSubForm] = useState(emptySub);
  const [subEditId, setSubEditId] = useState(null);
  const [devOpen, setDevOpen] = useState(false);
  const [devForm, setDevForm] = useState(emptyDev);
  const [devEditId, setDevEditId] = useState(null);

  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importNames, setImportNames] = useState({});
  const [committing, setCommitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [readingsDev, setReadingsDev] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [battOpen, setBattOpen] = useState(false);
  const [thresholds, setThresholds] = useState({ vla: 1.2, vrla: "", nicad: "", default: 1.2 });

  const loadAll = () => {
    api.get("/prc005/substations").then((r) => setSubs(r.data.substations));
    api.get("/prc005/devices").then((r) => setDevices(r.data.devices));
    api.get("/prc005/summary").then((r) => setSummary(r.data));
  };
  useEffect(() => { loadAll(); api.get("/prc005/reference").then((r) => setRef(r.data)); }, []);

  const codeOptions = useMemo(() => {
    const first = (devForm.component_type || "").trim().toLowerCase().split(" ")[0];
    if (!first) return ref.activity_codes;
    return ref.activity_codes.filter((c) => c.component_type.toLowerCase().startsWith(first));
  }, [devForm.component_type, ref.activity_codes]);
  const codeInfo = (code) => ref.activity_codes.find((c) => c.code === code);

  const shownDevices = filterSub === "all" ? devices : devices.filter((d) => d.substation_id === filterSub);

  // --- substation CRUD ---
  const openNewSub = () => { setSubForm(emptySub); setSubEditId(null); setSubOpen(true); };
  const openEditSub = (s) => { setSubForm({ ...emptySub, ...s }); setSubEditId(s.id); setSubOpen(true); };
  const saveSub = async () => {
    if (!subForm.name.trim()) return toast.error("Substation name required");
    try {
      if (subEditId) await api.put(`/prc005/substations/${subEditId}`, subForm);
      else await api.post("/prc005/substations", subForm);
      setSubOpen(false); loadAll(); toast.success("Substation saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeSub = async (id) => {
    if (!window.confirm("Delete this substation?")) return;
    try { await api.delete(`/prc005/substations/${id}`); loadAll(); toast.success("Substation deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // --- device CRUD ---
  const openNewDev = () => {
    setDevForm({ ...emptyDev, substation_id: filterSub !== "all" ? filterSub : (subs[0]?.id || "") });
    setDevEditId(null); setDevOpen(true);
  };
  const openEditDev = (d) => {
    setDevForm({ ...emptyDev, ...d, legacy_interval_years: d.legacy_interval_years ?? "",
      target_interval_months: d.target_interval_months ?? "", transitioned_date: d.transitioned_date || "",
      most_recent_date: d.most_recent_date || "", previous_date: d.previous_date || "" });
    setDevEditId(d.id); setDevOpen(true);
  };
  const saveDev = async () => {
    if (!devForm.substation_id) return toast.error("Select a substation");
    if (!devForm.activity_code) return toast.error("Select a maintenance activity code");
    const payload = {
      ...devForm,
      legacy_interval_years: devForm.legacy_interval_years === "" ? null : Number(devForm.legacy_interval_years),
      target_interval_months: devForm.target_interval_months === "" ? null : Number(devForm.target_interval_months),
      transitioned_date: devForm.transitioned_date || null,
      most_recent_date: devForm.most_recent_date || null,
      previous_date: devForm.previous_date || null,
    };
    try {
      if (devEditId) await api.put(`/prc005/devices/${devEditId}`, payload);
      else await api.post("/prc005/devices", payload);
      setDevOpen(false); loadAll(); toast.success("Device saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeDev = async (id) => {
    if (!window.confirm("Delete this device?")) return;
    await api.delete(`/prc005/devices/${id}`); loadAll(); toast.success("Device deleted");
  };

  const subName = (id) => subs.find((s) => s.id === id)?.name || "—";
  const st = summary.status || {};

  // --- export / seed / import ---
  const exportFile = async (format) => {
    try {
      const res = await api.get(`/prc005/export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const ext = format === "claro_pdf" ? "pdf" : "xlsx";
      const base = format === "nerc" ? "PRC-005-6_PSMP" : "prc005_register";
      a.href = url; a.download = `${base}_${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Export ready");
    } catch (e) { toast.error("Export failed. Please try again."); }
  };
  const seedSample = async () => {
    if (!window.confirm("Load anonymized demo substations & devices into this workspace?")) return;
    try { const { data } = await api.post("/prc005/seed-sample"); loadAll(); toast.success(`Loaded ${data.substations} substations, ${data.devices} devices`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true); setImportResult(null);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/prc005/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      const names = {}; (data.sheets || []).forEach((s) => { names[s.sheet] = s.suggested_substation; });
      setImportNames(names);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Import failed"); }
    finally { setImporting(false); e.target.value = ""; }
  };
  const commitImport = async () => {
    if (!importResult) return;
    setCommitting(true);
    try {
      const sheets = importResult.sheets
        .filter((s) => s.matched > 0)
        .map((s) => ({ sheet: s.sheet, substation_name: (importNames[s.sheet] || s.suggested_substation).trim(), devices: s.preview }));
      const { data } = await api.post("/prc005/import/commit", { filename: importResult.filename, sheets });
      setImportOpen(false); setImportResult(null); loadAll();
      toast.success(`Imported ${data.created} devices into ${data.substations.length} substation${data.substations.length === 1 ? "" : "s"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCommitting(false); }
  };
  const openHistory = async () => {
    setHistoryOpen(true);
    try { const { data } = await api.get("/prc005/import-history"); setHistory(data.history || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openBatterySettings = async () => {
    setBattOpen(true);
    try {
      const { data } = await api.get("/prc005/battery-settings");
      const t = data.sg_thresholds || {};
      setThresholds({ vla: t.vla ?? "", vrla: t.vrla ?? "", nicad: t.nicad ?? "", default: t.default ?? 1.2 });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveBatterySettings = async () => {
    try {
      const { data } = await api.put("/prc005/battery-settings", { sg_thresholds: thresholds });
      setBattOpen(false);
      toast.success("Battery thresholds saved");
      const t = data.sg_thresholds || {};
      setThresholds({ vla: t.vla ?? "", vrla: t.vrla ?? "", nicad: t.nicad ?? "", default: t.default ?? 1.2 });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="PRC-005 — Protection System Maintenance"
        subtitle="Substations, device inventory & maintenance intervals for the official NERC PRC-005-6 PSMP"
        action={canEdit && <div className="flex gap-2">
          <Button data-testid="prc005-import-btn" variant="outline" onClick={() => { setImportOpen(true); setImportResult(null); }} className="rounded-none"><UploadSimple size={16} className="mr-1" />Import</Button>
          <Button data-testid="new-substation-btn" variant="outline" onClick={openNewSub} className="rounded-none"><Buildings size={16} className="mr-1" />New Substation</Button>
          <Button data-testid="new-device-btn" onClick={openNewDev} disabled={subs.length === 0} className="rounded-none bg-primary hover:bg-primary/90"><Plus size={16} className="mr-1" />New Device</Button>
        </div>} />

      <div className="p-8 space-y-6">
        {/* export / utilities toolbar */}
        <div className="flex flex-wrap items-center gap-2 border border-border bg-secondary/40 px-4 py-3">
          {!isSubHidden("prc005:exports") && <>
          <span className="overline text-muted-foreground mr-1">Export</span>
          <Button data-testid="export-nerc-btn" variant="outline" size="sm" onClick={() => exportFile("nerc")} disabled={summary.devices === 0} className="rounded-none border-primary/60 text-primary"><DownloadSimple size={14} className="mr-1" />Texas RE / NERC Submission</Button>
          <span className="text-muted-foreground/50 text-xs px-1">|</span>
          <Button data-testid="export-claro-xlsx-btn" variant="outline" size="sm" onClick={() => exportFile("claro_xlsx")} disabled={summary.devices === 0} className="rounded-none"><FileXls size={14} className="mr-1" />Internal Register (Excel)</Button>
          <Button data-testid="export-claro-pdf-btn" variant="outline" size="sm" onClick={() => exportFile("claro_pdf")} disabled={summary.devices === 0} className="rounded-none"><FilePdf size={14} className="mr-1" />Internal Register (PDF)</Button>
          </>}
          {canEdit && <Button data-testid="battery-settings-btn" variant="ghost" size="sm" onClick={openBatterySettings} className="rounded-none ml-auto text-muted-foreground"><BatteryCharging size={14} className="mr-1" />Battery thresholds</Button>}
          {canEdit && <Button data-testid="import-history-btn" variant="ghost" size="sm" onClick={openHistory} className="rounded-none text-muted-foreground"><ClockCounterClockwise size={14} className="mr-1" />Import history</Button>}
          {canEdit && <Button data-testid="seed-sample-btn" variant="ghost" size="sm" onClick={seedSample} className="rounded-none text-muted-foreground"><Sparkle size={14} className="mr-1" />Load demo data</Button>}
        </div>
        {/* summary chips */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="prc005-summary">
          {[["Substations", summary.substations, "bg-card"], ["Devices", summary.devices, "bg-card"],
            ["Overdue", st.overdue || 0, "bg-red-50 border-red-300 text-red-700"],
            ["Due soon", st.due_soon || 0, "bg-amber-50 border-amber-300 text-amber-700"],
            ["On schedule", st.ok || 0, "bg-green-50 border-green-300 text-green-700"]].map(([l, v, cls]) => (
            <div key={l} className={`border border-border p-4 ${cls}`}>
              <div className="font-display font-black text-3xl tabular-nums">{v}</div>
              <div className="overline text-muted-foreground">{l}</div>
            </div>
          ))}
        </div>

        {/* substation list */}
        <div hidden={isSubHidden("prc005:substations")}>
          <div className="overline text-muted-foreground mb-2">Substations / Facilities</div>
          {subs.length === 0 ? <Empty title="No substations yet" hint="Add a substation (facility) to begin building your protection-system inventory." /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {subs.map((s) => (
                <div key={s.id} data-testid={`substation-card-${s.id}`} className="border border-border bg-card p-4">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className="font-display font-bold">{s.name}</div>
                      <div className="text-xs text-muted-foreground">{s.location || "—"} · {s.device_count} device{s.device_count === 1 ? "" : "s"}</div>
                    </div>
                    {canEdit && <div className="flex gap-2 shrink-0">
                      <button data-testid={`edit-substation-${s.id}`} onClick={() => openEditSub(s)} className="text-muted-foreground hover:text-foreground"><PencilSimple size={16} /></button>
                      <button data-testid={`delete-substation-${s.id}`} onClick={() => removeSub(s.id)} className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>
                    </div>}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {SUB_QUALIFIERS.filter(([k]) => s[k]).map(([k, l]) => <span key={k} className="text-[10px] border border-border px-1.5 py-0.5 text-muted-foreground">{l}</span>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* device inventory */}
        <div hidden={isSubHidden("prc005:devices")}>
          <div className="flex items-center justify-between mb-2">
            <div className="overline text-muted-foreground">Device Inventory</div>
            <Select value={filterSub} onValueChange={setFilterSub}>
              <SelectTrigger data-testid="device-substation-filter" className="rounded-none w-56 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All substations</SelectItem>
                {subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {shownDevices.length === 0 ? <Empty title="No devices" hint="Add a protection-system device and assign its maintenance activity code." /> : (
            <div className="border border-border overflow-x-auto bg-card">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60 text-left">
                  <tr className="overline text-muted-foreground">
                    <th className="px-3 py-2">Substation</th><th className="px-3 py-2">Component</th>
                    <th className="px-3 py-2">Device ID</th><th className="px-3 py-2">Code</th>
                    <th className="px-3 py-2">Program Int.</th><th className="px-3 py-2">Last Maint.</th>
                    <th className="px-3 py-2">Target Due</th><th className="px-3 py-2">Program Due</th>
                    <th className="px-3 py-2">Status</th><th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {shownDevices.map((d) => (
                    <tr key={d.id} data-testid={`device-row-${d.id}`} className="border-t border-border hover:bg-secondary/30">
                      <td className="px-3 py-2">{d.substation_name || subName(d.substation_id)}</td>
                      <td className="px-3 py-2">{d.component_type}<div className="text-[11px] text-muted-foreground">{d.sub_type}</div></td>
                      <td className="px-3 py-2 font-mono">{d.unique_identifier || "—"}</td>
                      <td className="px-3 py-2"><span className="font-mono font-bold">{d.activity_code}</span></td>
                      <td className="px-3 py-2 text-xs">{d.program_interval_months ? `${d.program_interval_months} mo` : "—"}</td>
                      <td className="px-3 py-2 text-xs">{d.most_recent_date || "—"}</td>
                      <td className="px-3 py-2 text-xs">{d.target_due_date || "—"}</td>
                      <td className="px-3 py-2 text-xs font-semibold">{d.program_due_date || "—"}</td>
                      <td className="px-3 py-2"><StatusBadge status={d.status} /></td>
                      <td className="px-3 py-2 text-right">{canEdit && <div className="flex gap-2 justify-end items-center">
                        {d.battery_readings && d.battery_readings.length > 0 && (
                          <button data-testid={`readings-device-${d.id}`} onClick={() => setReadingsDev(d)} title={`${d.battery_readings.length} battery readings`} className={`relative ${d.battery_sg_flags ? "text-red-600" : "text-muted-foreground hover:text-foreground"}`}>
                            <BatteryCharging size={16} />{d.battery_sg_flags ? <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[8px] rounded-full w-3.5 h-3.5 flex items-center justify-center">{d.battery_sg_flags}</span> : null}
                          </button>
                        )}
                        <button data-testid={`edit-device-${d.id}`} onClick={() => openEditDev(d)} className="text-muted-foreground hover:text-foreground"><PencilSimple size={15} /></button>
                        <button data-testid={`delete-device-${d.id}`} onClick={() => removeDev(d.id)} className="text-muted-foreground hover:text-red-600"><Trash size={15} /></button>
                      </div>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Substation dialog */}
      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{subEditId ? "Edit" : "New"} Substation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="substation-name-input" value={subForm.name} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>Location</Label><Input value={subForm.location} onChange={(e) => setSubForm({ ...subForm, location: e.target.value })} className="rounded-none mt-1" /></div>
            <div>
              <Label>Protection System components present (qualifying questions)</Label>
              <div className="grid grid-cols-2 gap-2 mt-2 border border-border p-3">
                {SUB_QUALIFIERS.map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox data-testid={`sub-qualifier-${k}`} checked={!!subForm[k]} onCheckedChange={(v) => setSubForm({ ...subForm, [k]: !!v })} className="rounded-none" />{l}
                  </label>
                ))}
              </div>
            </div>
            <div><Label>Notes</Label><Textarea value={subForm.notes} onChange={(e) => setSubForm({ ...subForm, notes: e.target.value })} className="rounded-none mt-1" /></div>
            <Button data-testid="save-substation-btn" onClick={saveSub} className="rounded-none w-full bg-primary hover:bg-primary/90">Save Substation</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Device dialog */}
      <Dialog open={devOpen} onOpenChange={setDevOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{devEditId ? "Edit" : "New"} Protection System Device</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Substation</Label>
                <Select value={devForm.substation_id} onValueChange={(v) => setDevForm({ ...devForm, substation_id: v })}>
                  <SelectTrigger data-testid="device-substation-select" className="rounded-none mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Component Type</Label>
                <Select value={devForm.component_type} onValueChange={(v) => setDevForm({ ...devForm, component_type: v, activity_code: "" })}>
                  <SelectTrigger data-testid="device-component-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{ref.component_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Maintenance Activity Code (sets the program / max interval)</Label>
              <Select value={devForm.activity_code} onValueChange={(v) => setDevForm({ ...devForm, activity_code: v })}>
                <SelectTrigger data-testid="device-activity-code-select" className="rounded-none mt-1"><SelectValue placeholder="Select code…" /></SelectTrigger>
                <SelectContent>{codeOptions.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.sub_type} ({c.interval_label})</SelectItem>)}</SelectContent>
              </Select>
              {devForm.activity_code && codeInfo(devForm.activity_code) && (
                <p data-testid="device-interval-hint" className="text-[11px] text-muted-foreground mt-1">{codeInfo(devForm.activity_code).table} · Max interval: <b>{codeInfo(devForm.activity_code).interval_label}</b></p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Unique Identifier</Label><Input data-testid="device-uid-input" value={devForm.unique_identifier} onChange={(e) => setDevForm({ ...devForm, unique_identifier: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Manufacturer & Model</Label><Input value={devForm.manufacturer_model} onChange={(e) => setDevForm({ ...devForm, manufacturer_model: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Monitored Status</Label>
                <Select value={devForm.monitored_status} onValueChange={(v) => setDevForm({ ...devForm, monitored_status: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{ref.monitored_status.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-4 pb-2">
                {[["ras", "RAS"], ["ufls", "UFLS"], ["uvls", "UVLS"]].map(([k, l]) => (
                  <label key={k} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <Checkbox data-testid={`device-${k}`} checked={!!devForm[k]} onCheckedChange={(v) => setDevForm({ ...devForm, [k]: !!v })} className="rounded-none" />{l}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Most Recent Maintenance Date</Label><Input data-testid="device-recent-date" type="date" value={devForm.most_recent_date || ""} onChange={(e) => setDevForm({ ...devForm, most_recent_date: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Most Recent Evidence File Name</Label><Input value={devForm.most_recent_evidence} onChange={(e) => setDevForm({ ...devForm, most_recent_evidence: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Previous Maintenance Date</Label><Input type="date" value={devForm.previous_date || ""} onChange={(e) => setDevForm({ ...devForm, previous_date: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Previous Evidence File Name</Label><Input value={devForm.previous_evidence} onChange={(e) => setDevForm({ ...devForm, previous_evidence: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Target Interval (months)</Label><Input data-testid="device-target-interval" type="number" value={devForm.target_interval_months} onChange={(e) => setDevForm({ ...devForm, target_interval_months: e.target.value })} className="rounded-none mt-1" placeholder="e.g. 120" /></div>
              <div><Label>Transitioned Date</Label><Input type="date" value={devForm.transitioned_date || ""} onChange={(e) => setDevForm({ ...devForm, transitioned_date: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Legacy Interval (yrs)</Label><Input type="number" value={devForm.legacy_interval_years} onChange={(e) => setDevForm({ ...devForm, legacy_interval_years: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <div><Label>Comments</Label><Textarea value={devForm.comments} onChange={(e) => setDevForm({ ...devForm, comments: e.target.value })} className="rounded-none mt-1" /></div>
            <Button data-testid="save-device-btn" onClick={saveDev} className="rounded-none w-full bg-primary hover:bg-primary/90">Save Device</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Import dialog (multi-sheet) */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkle size={18} className="text-primary" />AI-Assisted Import</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Upload a PRC-005 / PSMP workbook (.xlsx). ClaroShield reads <b>every tab</b> and creates one substation per tab. Battery cell inspection &amp; discharge readings roll up under the battery bank (specific gravity below 1.200 is flagged).</p>
            <div className="border border-dashed border-border p-4 text-center">
              <input data-testid="import-file-input" type="file" accept=".xlsx,.xlsm" onChange={onImportFile} className="text-sm" />
              {importing && <div className="text-sm text-muted-foreground mt-2">Analyzing workbook…</div>}
            </div>
            {importResult && (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="import-summary">
                  <div className="text-sm"><b>{importResult.filename}</b> · {importResult.total_sheets} tab{importResult.total_sheets === 1 ? "" : "s"} · <b>{importResult.total_devices}</b> devices</div>
                  <Button data-testid="commit-import-btn" onClick={commitImport} disabled={committing || !importResult.total_devices} className="rounded-none bg-primary hover:bg-primary/90">{committing ? "Importing…" : `Import all ${importResult.total_devices} devices`}</Button>
                </div>
                {importResult.sheets.map((s) => (
                  <div key={s.sheet} className="border border-border" data-testid={`import-sheet-${s.sheet}`}>
                    <div className="bg-secondary/50 px-3 py-2 flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                        <Buildings size={15} className="text-muted-foreground shrink-0" />
                        <span className="text-xs text-muted-foreground shrink-0">Tab “{s.sheet}” →</span>
                        <Input data-testid={`import-subname-${s.sheet}`} value={importNames[s.sheet] ?? s.suggested_substation}
                          onChange={(e) => setImportNames({ ...importNames, [s.sheet]: e.target.value })}
                          className="rounded-none h-7 text-sm max-w-xs" />
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <b>{s.matched}</b> devices{s.battery_readings_count ? <> · <b>{s.battery_readings_count}</b> battery readings</> : null}{s.battery_sg_flags ? <span className="text-red-600 font-semibold"> · {s.battery_sg_flags} SpGr&lt;1.200</span> : null}
                      </div>
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-secondary/30 text-left sticky top-0"><tr className="overline text-muted-foreground">
                          <th className="px-2 py-1.5">Device ID</th><th className="px-2 py-1.5">Component</th><th className="px-2 py-1.5">Code</th><th className="px-2 py-1.5">Interval</th><th className="px-2 py-1.5">Mfr/Model</th>
                        </tr></thead>
                        <tbody>
                          {s.preview.map((p, i) => (
                            <tr key={`${s.sheet}-${p.unique_identifier}-${i}`} className="border-t border-border">
                              <td className="px-2 py-1 font-mono">{p.unique_identifier || "—"}</td>
                              <td className="px-2 py-1">{p.component_type}</td>
                              <td className="px-2 py-1 font-mono font-bold">{p.activity_code || "—"}{p.activity_code_suggested && <span className="ml-1 text-[9px] font-sans font-semibold text-primary bg-primary/10 px-1 py-0.5 rounded-sm align-middle">auto</span>}</td>
                              <td className="px-2 py-1">{p.suggested_interval_label || "—"}{p.target_interval_months ? <span className="text-muted-foreground"> · tgt {p.target_interval_months}mo</span> : null}</td>
                              <td className="px-2 py-1">{p.manufacturer_model || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground">Codes marked <b className="text-primary">auto</b> are suggested from component type, monitoring and interval — review/adjust per device after import. AI mapping is a best-effort starting point.</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Battery readings drill-in */}
      <Dialog open={!!readingsDev} onOpenChange={(o) => { if (!o) setReadingsDev(null); }}>
        <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="battery-readings-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><BatteryCharging size={18} className="text-primary" />Battery Readings — {readingsDev?.unique_identifier}</DialogTitle></DialogHeader>
          {readingsDev && (() => {
            const rd = readingsDev.battery_readings || [];
            const insp = rd.filter((r) => r.type === "inspection");
            const disch = rd.filter((r) => r.type === "discharge");
            return (
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">{rd.length} readings · {readingsDev.battery_sg_flags ? <span className="text-red-600 font-semibold">{readingsDev.battery_sg_flags} with specific gravity below 1.200</span> : "no specific-gravity exceptions"}.</div>
                {insp.length > 0 && <div>
                  <div className="overline text-muted-foreground mb-1">Cell Inspections</div>
                  <div className="border border-border max-h-72 overflow-y-auto"><table className="w-full text-xs">
                    <thead className="bg-secondary/60 text-left sticky top-0"><tr className="overline text-muted-foreground">
                      <th className="px-2 py-1.5">Date</th><th className="px-2 py-1.5">Cycle</th><th className="px-2 py-1.5">Cell</th><th className="px-2 py-1.5">Float V</th><th className="px-2 py-1.5">Temp °F</th><th className="px-2 py-1.5">Sp.Gr.</th><th className="px-2 py-1.5">Status</th><th className="px-2 py-1.5">By</th>
                    </tr></thead>
                    <tbody>{insp.map((r, i) => (
                      <tr key={i} data-testid={`reading-row-${i}`} className={`border-t border-border ${r.flag ? "bg-red-50" : ""}`}>
                        <td className="px-2 py-1">{r.date || "—"}</td><td className="px-2 py-1">{r.cycle || "—"}</td><td className="px-2 py-1">{r.cell || "—"}</td>
                        <td className="px-2 py-1">{r.float_volts ?? "—"}</td><td className="px-2 py-1">{r.temp_f ?? "—"}</td>
                        <td className={`px-2 py-1 font-semibold ${r.flag ? "text-red-600" : ""}`}>{r.specific_gravity ?? "—"}{r.flag && <Warning size={11} className="inline ml-1 mb-0.5" />}</td>
                        <td className="px-2 py-1">{r.status || "—"}</td><td className="px-2 py-1">{r.inspector || "—"}</td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                </div>}
                {disch.length > 0 && <div>
                  <div className="overline text-muted-foreground mb-1">Discharge Test</div>
                  <div className="border border-border max-h-56 overflow-y-auto"><table className="w-full text-xs">
                    <thead className="bg-secondary/60 text-left sticky top-0"><tr className="overline text-muted-foreground">
                      <th className="px-2 py-1.5">Elapsed</th><th className="px-2 py-1.5">String V</th><th className="px-2 py-1.5">Discharge A</th><th className="px-2 py-1.5">Lowest Cell</th><th className="px-2 py-1.5">Observation</th>
                    </tr></thead>
                    <tbody>{disch.map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-2 py-1">{r.elapsed || "—"}</td><td className="px-2 py-1">{r.string_voltage ?? "—"}</td><td className="px-2 py-1">{r.discharge_current ?? "—"}</td><td className="px-2 py-1">{r.lowest_cell || "—"}</td><td className="px-2 py-1">{r.observation || "—"}</td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                </div>}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Import history */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="import-history-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ClockCounterClockwise size={18} className="text-primary" />Import History</DialogTitle></DialogHeader>
          {history.length === 0 ? <Empty title="No imports yet" hint="Spreadsheet imports will be logged here for audit traceability." /> : (
            <div className="border border-border"><table className="w-full text-xs">
              <thead className="bg-secondary/60 text-left"><tr className="overline text-muted-foreground">
                <th className="px-3 py-2">When</th><th className="px-3 py-2">File</th><th className="px-3 py-2">Substations</th><th className="px-3 py-2">Devices</th><th className="px-3 py-2">By</th>
              </tr></thead>
              <tbody>{history.map((h) => (
                <tr key={h.id} data-testid={`history-row-${h.id}`} className="border-t border-border align-top">
                  <td className="px-3 py-2 whitespace-nowrap">{(h.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 font-mono">{h.filename || "—"}</td>
                  <td className="px-3 py-2">{(h.sheets || []).map((s) => `${s.substation} (${s.devices})`).join(", ")}</td>
                  <td className="px-3 py-2 font-semibold tabular-nums">{h.total_devices}</td>
                  <td className="px-3 py-2">{h.imported_by || "—"}</td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </DialogContent>
      </Dialog>

      {/* Battery SpGr thresholds */}
      <Dialog open={battOpen} onOpenChange={setBattOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="battery-settings-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><BatteryCharging size={18} className="text-primary" />Battery Specific-Gravity Thresholds</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">A cell reading below its chemistry's threshold is flagged and raises a ClaroFlow alert on import. Leave blank to disable flagging for a chemistry (e.g. sealed VRLA / NiCad have no measurable specific gravity).</p>
            {[["vla", "Flooded Lead-Acid (VLA)"], ["vrla", "Valve-Regulated (VRLA)"], ["nicad", "Nickel-Cadmium (NiCad)"], ["default", "Default / Other"]].map(([k, label]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <Label className="text-sm">{label}</Label>
                <Input data-testid={`threshold-${k}`} type="number" step="0.001" placeholder={k === "default" ? "1.200" : "disabled"}
                  value={thresholds[k]} onChange={(e) => setThresholds({ ...thresholds, [k]: e.target.value })}
                  className="rounded-none w-28 text-right" />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBattOpen(false)} className="rounded-none">Cancel</Button>
            <Button data-testid="save-battery-settings-btn" onClick={saveBatterySettings} className="rounded-none bg-primary hover:bg-primary/90">Save thresholds</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
